import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoDomandaDTO.ts';
import { EngineThemeProvider } from '@framework/engine/ui/theme/EngineThemeProvider.tsx';
import { FormCoordinator } from '@framework/form/form/coordinator/FormCoordinatorContext.tsx';
import { ModuleFormSectionsMonitorContextProvider } from '@framework/frontoffice-submission/module/form/sections-monitor/ModuleFormSectionsMonitorContext.tsx';
import { useSubmissionNavigation } from '@framework/frontoffice-submission/navigation/useSubmissionNavigation.ts';
import { SubmissionPaymentsCard } from '@framework/frontoffice-submission/payments/SubmissionPaymentsCard.tsx';
import { FlexSubmissionRow } from '@framework/frontoffice-submission/submission/FlexSubmissionRow.tsx';
import { buildReportMenuItem } from '@framework/frontoffice-submission/submission/hooks/buildReportMenuItem.ts';
import { type ISubmissionMenuItem } from '@framework/frontoffice-submission/submission/menu/ISubmissionMenuItem.tsx';
import { SubmissionMenu } from '@framework/frontoffice-submission/submission/menu/SubmissionMenu.tsx';
import { SubmissionSummaryLogic } from '@framework/frontoffice-submission/submission/signed-summary/SubmissionSummaryLogic.ts';
import { SubmissionSummarySigner } from '@framework/frontoffice-submission/submission/signed-summary/SubmissionSummarySigner.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import {
  generatePath,
  Redirect,
  Route,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { IconListDetails } from '@tabler/icons-react';
import { Flex } from 'antd';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from 'react-router';
import { CompilationModuleCard } from '../../../components/submissions/cards/module/CompilationModuleCard.tsx';
import { SubmissionSummary } from '../../../components/submissions/cards/send/SubmissionSummary.tsx';
import { getSubmissionMenuLink } from '../../../components/submissions/hooks/getSubmissionMenuLink.ts';

export interface ISubmissionSentTabRequestProps {
  active?: boolean;
  submissionModuleId?: string | null;
  submission: RichiestaDTO | null | undefined;
  menuItems: ISubmissionMenuItem[];
  children?: React.ReactNode;
  initialModuleId?: string | null | undefined;
  submissionModules: RichiestaModuloDTO[];
  /**
   * Tipo domanda: usato per decidere se mostrare il Riepilogo (solo se il
   * riepilogo è configurato come ESTESO) e per idratarne dati/personalizzazioni.
   */
  inquiryType?: TipoDomandaDTO;
}

/**
 * Tab "La mia richiesta" della richiesta dopo l'invio (sola lettura).
 *
 * Se la domanda ha `modalitaRiepilogo === ESTESO`, la prima voce di menu /
 * prima pagina è il Riepilogo della Richiesta (come nella compilazione FO);
 * altrimenti la prima pagina resta il primo modulo.
 */
export function SubmissionSentTabRequest(
  props: ISubmissionSentTabRequestProps,
) {
  const {
    active,
    submission,
    submissionModuleId,
    menuItems,
    initialModuleId,
    submissionModules,
    inquiryType,
  } = props;
  const { t } = useTranslation();

  const submissionId = submission?.id;

  // Il Riepilogo nella vista "inviata" si mostra solo con riepilogo ESTESO,
  // coerentemente con la compilazione FO (vista di riferimento lato cittadino).
  const showReport =
    SubmissionSummaryLogic.isExtendedSummaryEnabled(inquiryType);

  // Voce "Riepilogo" in testa al menu: l'hook condiviso non la emette quando la
  // richiesta è inviata, quindi la prependiamo qui con il builder condiviso
  // `buildReportMenuItem` (etichetta e icona proprie del Frontoffice).
  const finalMenuItems = useMemo<ISubmissionMenuItem[]>(() => {
    if (!(showReport && submissionId)) return menuItems;

    const reportMenuItem = buildReportMenuItem({
      submissionId,
      getMenuLink: getSubmissionMenuLink,
      name: t('menu-items.riepilogo', 'Riepilogo'),
      icon: <TbIcon icon={IconListDetails} />,
      options: { isSent: true },
    });

    return [reportMenuItem, ...menuItems];
  }, [showReport, submissionId, menuItems, t]);

  const handleNavigation = useSubmissionNavigation({
    submissionId,
    steps: finalMenuItems,
  });

  if (!active) return null;

  const configurations = inquiryType?.postCompilazione?.riepilogo;

  return (
    <FormCoordinator>
      <ModuleFormSectionsMonitorContextProvider>
        <FlexSubmissionRow
          menuSide="right"
          menu={
            <SubmissionMenu
              items={finalMenuItems}
              currentSubmissionModuleId={
                submissionModuleId ?? initialModuleId ?? '<unknown>'
              }
            />
          }
        >
          <EngineThemeProvider>
            <Switch>
              {/* Prima pagina: Riepilogo se ESTESO, altrimenti primo modulo. */}
              {showReport && submissionId ? (
                <Redirect
                  exact
                  path="/richieste/:id/inviata"
                  to={generatePath('/richieste/:id/inviata/report', {
                    id: submissionId,
                  })}
                />
              ) : (
                <Route
                  exact
                  path="/richieste/:id/inviata"
                  render={(props: any) => (
                    <CompilationModuleCard
                      {...props}
                      initialModuleId={initialModuleId}
                      handleNavigation={handleNavigation}
                      readonly
                    />
                  )}
                />
              )}

              {/* Riepilogo della Richiesta (sola lettura, solo se ESTESO) */}
              {showReport && (
                <Route
                  exact
                  path="/richieste/:id/inviata/report"
                  render={() => (
                    <Flex vertical gap={24}>
                      <SubmissionSummary
                        text={configurations?.testo}
                        submission={submission ?? undefined}
                        modules={submissionModules}
                        hasInlineDownload={submissionModules.length > 1}
                        submissionId={submissionId ?? undefined}
                        inquiryType={inquiryType}
                        customizations={configurations?.personalizzazioni}
                        getModuleLink={(sid, mid) =>
                          generatePath(
                            '/richieste/:id/inviata/moduli/:moduleId',
                            { id: sid, moduleId: mid },
                          )
                        }
                      />
                      <SubmissionSummarySigner
                        submission={submission}
                        inquiryType={inquiryType}
                        downloadOnly
                      />
                    </Flex>
                  )}
                />
              )}

              <Route
                exact
                path="/richieste/:id/inviata/moduli/:moduleId"
                render={(props: any) => (
                  <CompilationModuleCard
                    {...props}
                    handleNavigation={handleNavigation}
                    readonly
                  />
                )}
              />

              <Route
                exact
                path="/richieste/:id/inviata/pagamenti"
                render={() => (
                  <SubmissionPaymentsCard
                    submissionId={submissionId}
                    handleNavigation={handleNavigation}
                    submission={submission}
                  />
                )}
              />
            </Switch>
          </EngineThemeProvider>
        </FlexSubmissionRow>
      </ModuleFormSectionsMonitorContextProvider>
    </FormCoordinator>
  );
}
